/**
 * Ковёр на плане — компонент раздела «Декор».
 *
 * В отличие от мебели, у ковра нет белой подложки: он лежит на полу и должен
 * быть виден своим цветом, полупрозрачно, чтобы под ним читалась укладка пола.
 * Поле — цвет предмета (выбранный или из каталога), по краю кайма цвета из каталога,
 * у прямоугольного — бахрома по коротким сторонам. Толщины линий — в экранных
 * пикселях через `mmPerPx`. Рисуется в локальных координатах, поворот делает слой.
 */
import type { PointerEvent } from "react";
import { itemDimensions, type FloorItem } from "@/shared/plan";
import { RugShape as RugForm, decorItemById, type CatalogItem } from "@/shared/catalog";

/** Ширина каймы, мм */
const BORDER = 120;
/** Длина бахромы и шаг нитей, мм */
const FRINGE = 60;
const FRINGE_STEP = 50;
const SELECTED = "#2f6feb";

interface Props {
	item: FloorItem;
	info: CatalogItem | null;
	selected: boolean;
	mmPerPx: number;
	interactive?: boolean;
	onPointerDown?: (e: PointerEvent<SVGGElement>) => void;
}

export const RugShape = ({
	item,
	info,
	selected,
	mmPerPx,
	interactive = true,
	onPointerDown,
}: Props) => {
	const decor = info ? decorItemById(info.id) : null;
	const size = info ? itemDimensions(item, info) : { width: 2000, depth: 3000 };
	const w = size.width;
	const d = size.depth;
	const field = item.color ?? info?.color ?? "#c8b8a0";
	const border = decor?.rug?.border ?? "#7a5f49";
	const round = decor?.rug?.shape === RugForm.Round;
	const outline = selected ? SELECTED : border;
	const stroke = 1.2 * mmPerPx;

	return (
		<g
			onPointerDown={onPointerDown}
			style={{
				cursor: interactive ? "move" : "default",
				pointerEvents: interactive ? "auto" : "none",
			}}
		>
			{round ? (
				<>
					<ellipse rx={w / 2} ry={d / 2} fill={border} opacity={0.55} />
					<ellipse rx={w / 2 - BORDER} ry={d / 2 - BORDER} fill={field} opacity={0.6} />
					<ellipse rx={w / 2} ry={d / 2} fill="none" stroke={outline} strokeWidth={stroke} />
				</>
			) : (
				<>
					<rect x={-w / 2} y={-d / 2} width={w} height={d} fill={border} opacity={0.55} />
					<rect
						x={-w / 2 + BORDER}
						y={-d / 2 + BORDER}
						width={w - 2 * BORDER}
						height={d - 2 * BORDER}
						fill={field}
						opacity={0.6}
					/>
					<rect
						x={-w / 2}
						y={-d / 2}
						width={w}
						height={d}
						fill="none"
						stroke={outline}
						strokeWidth={stroke}
					/>
					{/* Бахрома по коротким сторонам — за габаритом ковра */}
					{[-1, 1].map((side) => (
						<path
							key={side}
							d={Array.from({ length: Math.floor(w / FRINGE_STEP) + 1 }, (_, i) => {
								const x = -w / 2 + i * FRINGE_STEP;
								const y = (side * d) / 2;
								return `M${x} ${y}v${side * FRINGE}`;
							}).join("")}
							stroke={outline}
							strokeWidth={0.8 * mmPerPx}
							style={{ pointerEvents: "none" }}
						/>
					))}
				</>
			)}
		</g>
	);
};
